import { __awaiter } from 'tslib';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import * as i2 from 'se-fe-api';
import { SeFeApiModule } from 'se-fe-api';

function sanitizeFileName(fileName) {
    const newFileName = fileName.replace(/[^a-zA-Z0-9_() .-]+/g, '');
    const fileExtension = `.${newFileName.split('.')[1]}`;
    return newFileName === fileExtension ? `file${fileExtension}` : newFileName;
}
class SeFeAttachment {
    constructor(file) {
        this.guid = '';
        if (file instanceof File) {
            this.guid = '';
            this.file = file;
            this.filename = sanitizeFileName(file.name);
            this.size = file.size;
            this.content_type = file.type; // eslint-disable-line @typescript-eslint/camelcase
            this.status = 'uploading';
        }
        else {
            this.status = 'generic_error';
        }
    }
}

class SeFeAttachmentError extends Error {
}
class SeFeAttachmentFileMissingError extends SeFeAttachmentError {
}
class SeFeAttachmentFileTypeInvalidError extends SeFeAttachmentError {
}
class SeFeAttachmentFileTooLargeError extends SeFeAttachmentError {
}
class SeFeAttachmentService {
    constructor(http, apiService, env) {
        this.http = http;
        this.apiService = apiService;
        this.env = env;
    }
    upload(attachment, assetType = SeFeAttachmentService.ATTACHMENT) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(attachment === null || attachment === void 0 ? void 0 : attachment.file))
                throw new SeFeAttachmentFileMissingError();
            const presignResult = yield this.presignFile(attachment.file, assetType);
            const guid = ((_c = (((_b = (_a = presignResult === null || presignResult === void 0 ? void 0 : presignResult.result) === null || _a === void 0 ? void 0 : _a.fields) === null || _b === void 0 ? void 0 : _b.key) || '').split('/')) === null || _c === void 0 ? void 0 : _c[1]) || '';
            const url = this.getUrl(this.env.assetService, this.getObjectUrlPath(guid, attachment.filename));
            yield this.uploadFile(attachment.file, presignResult === null || presignResult === void 0 ? void 0 : presignResult.result);
            Object.assign(attachment, { guid, url, status: 'uploaded' });
            return attachment;
        });
    }
    presignFile(file, assetType) {
        return __awaiter(this, void 0, void 0, function* () {
            const opts = {
                withCredentials: true,
                headers: { Accept: 'application/json' },
            };
            const data = {
                file_name: file.name,
                file_size: file.size,
                asset_type: assetType,
            };
            try {
                return yield this.apiService.post('v3/assets/presign-post', data, opts).toPromise();
            }
            catch (err) {
                this.handleUploadErrors(err);
            }
        });
    }
    uploadFile(file, { url, fields: data }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let formData = new FormData();
            Object.entries(data).forEach(([key, val]) => formData.append(key, val));
            formData.append('file', file);
            let options = {
                observe: 'response',
                // TODO: Content-Type for uploaded file is returned as binary/octet-stream from S3
                // headers: { ContentType: undefined /* data.ContentType */ }, // let the browser set this, it adds boundary
            };
            try {
                yield ((_a = this.http.post(url, formData, options)) === null || _a === void 0 ? void 0 : _a.toPromise());
            }
            catch (err) {
                console.error('WHAT WAS I TRYING TO SEND', [url, formData, options]);
                this.handleUploadErrors(err);
            }
        });
    }
    // Utility functions
    getObjectUrlPath(guid, fileName) {
        return `attachment/${guid}/${encodeURIComponent(fileName).replace(/%20/g, '+')}`;
    }
    getUrl(base, path) {
        return String(Object.assign(new URL(path, base), { protocol: 'https' }));
    }
    handleUploadErrors(err) {
        var _a;
        console.error('ERROR', err);
        if (!err.status)
            throw err; // Not a network error
        if (((_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.result) === 'Invalid File')
            throw new SeFeAttachmentFileTypeInvalidError();
        if (this.findXmlErrorCode(err === null || err === void 0 ? void 0 : err.data) === 'EntityTooLarge')
            throw new SeFeAttachmentFileTooLargeError();
        throw new SeFeAttachmentError(); // Unexpected error type, throw generic
    }
    findXmlErrorCode(xml) {
        var _a;
        const xmlDoc = new DOMParser().parseFromString(xml, 'text/xml');
        return ((_a = xmlDoc.evaluate('//Error/Code', xmlDoc, null, XPathResult.STRING_TYPE)) === null || _a === void 0 ? void 0 : _a.stringValue) || '';
    }
}
SeFeAttachmentService.ATTACHMENT = 'attachment';
SeFeAttachmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentService, deps: [{ token: i1.HttpClient }, { token: i2.SeFeApiService }, { token: 'env' }], target: i0.ɵɵFactoryTarget.Injectable });
SeFeAttachmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.SeFeApiService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }]; } });

class SeFeAttachmentServiceModule {
    static forRoot(environment) {
        return {
            ngModule: SeFeAttachmentServiceModule,
            providers: [
                { provide: 'env', useValue: environment }
            ]
        };
    }
}
SeFeAttachmentServiceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentServiceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeAttachmentServiceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentServiceModule, imports: [SeFeApiModule] });
SeFeAttachmentServiceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentServiceModule, imports: [[SeFeApiModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAttachmentServiceModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SeFeApiModule]
                }]
        }] });

/*
 * Public API Surface of se-fe-attachment-service
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeAttachment, SeFeAttachmentError, SeFeAttachmentFileMissingError, SeFeAttachmentFileTooLargeError, SeFeAttachmentFileTypeInvalidError, SeFeAttachmentService, SeFeAttachmentServiceModule };
